import { QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from './components/ErrorBoundary';
import { usePath } from './hooks/usePath';
import { AdminLayout } from './layouts/AdminLayout';
import { AuthProvider } from './lib/auth';
import { useAuthStore } from './lib/auth-store';
import { queryClient } from './lib/queryClient';
import { AgentDetail } from './pages/AgentDetail';
import { AgentMonitoring } from './pages/AgentMonitoring';
import { AgentPrompts } from './pages/AgentPrompts';
import { AgentTools } from './pages/AgentTools';
import { AuditLog } from './pages/AuditLog';
import { Blog } from './pages/Blog';
import { Changelog } from './pages/Changelog';
import { ChatDashboard } from './pages/ChatDashboard';
import { ChatRunDetail } from './pages/ChatRunDetail';
import { Costs } from './pages/Costs';
import { FreeCapacity } from './pages/FreeCapacity';
import { LeadAgentTimeline } from './pages/LeadAgentTimeline';
import { Login } from './pages/Login';
import { ModelRoutes } from './pages/ModelRoutes';
import { OrganizationDetail } from './pages/OrganizationDetail';
import { Organizations } from './pages/Organizations';
import { Overview } from './pages/Overview';
import { PendingToolActions } from './pages/PendingToolActions';
import { PipelineAgents } from './pages/PipelineAgents';
import { PlatformHealth } from './pages/PlatformHealth';
import { RegulatoryApprovals } from './pages/RegulatoryApprovals';
import { VoiceLanguageMappings } from './pages/VoiceLanguageMappings';
import { ReleaseDetail } from './pages/ReleaseDetail';
import { Releases } from './pages/Releases';
import { Settings } from './pages/Settings';
import { Subscriptions } from './pages/Subscriptions';
import { UnassignedLeads } from './pages/UnassignedLeads';
import { UserDetail } from './pages/UserDetail';
import { Users } from './pages/Users';
import { VoiceCallDetail } from './pages/VoiceCallDetail';
import { VoiceCalls } from './pages/VoiceCalls';
import { Waitlist } from './pages/Waitlist';
import { Workflows } from './pages/Workflows';

function Router() {
  const path = usePath();

  if (path.startsWith('/pipeline-agents/')) {
    const parts = path.replace('/pipeline-agents/', '').split('/');
    if (parts.length === 2) {
      return <AgentDetail organizationId={parts[0]} agentType={parts[1]} />;
    }
  }

  if (path.startsWith('/organizations/')) {
    const id = path.replace('/organizations/', '');
    return <OrganizationDetail id={id} />;
  }

  if (path.startsWith('/chat-dashboard/')) {
    const runId = path.replace('/chat-dashboard/', '');
    return <ChatRunDetail runId={runId} />;
  }

  if (path.startsWith('/users/')) {
    const id = path.replace('/users/', '');
    return <UserDetail id={id} />;
  }

  if (path.startsWith('/voice-calls/')) {
    const id = path.replace('/voice-calls/', '');
    return <VoiceCallDetail id={id} />;
  }

  if (path.startsWith('/releases/')) {
    const id = path.replace('/releases/', '');
    return <ReleaseDetail id={id} />;
  }

  switch (path) {
    case '/organizations':
      return <Organizations />;
    case '/subscriptions':
      return <Subscriptions />;
    case '/costs':
      return <Costs />;
    case '/prompts':
      return <AgentPrompts />;
    case '/agent-tools':
      return <AgentTools />;
    case '/model-routes':
      return <ModelRoutes />;
    case '/agent-monitoring':
      return <AgentMonitoring />;
    case '/workflows':
      return <Workflows />;
    case '/chat-dashboard':
      return <ChatDashboard />;
    case '/voice-calls':
      return <VoiceCalls />;
    case '/lead-timeline':
      return <LeadAgentTimeline />;
    case '/pipeline-agents':
      return <PipelineAgents />;
    case '/platform-health':
      return <PlatformHealth />;
    case '/pending-tools':
      return <PendingToolActions />;
    case '/regulatory-approvals':
      return <RegulatoryApprovals />;
    // The path QA navigated to by hand across four rounds, kept verbatim so
    // their existing steps land somewhere real (FLOSEAI-367).
    case '/admin-voice-language-mappings':
      return <VoiceLanguageMappings />;
    case '/unassigned-leads':
      return <UnassignedLeads />;
    case '/users':
      return <Users />;
    case '/free-capacity':
      return <FreeCapacity />;
    case '/waitlist':
      return <Waitlist />;
    case '/releases':
      return <Releases />;
    case '/audit-log':
      return <AuditLog />;
    case '/changelog':
      return <Changelog />;
    case '/blog':
      return <Blog />;
    case '/settings':
      return <Settings />;
    default:
      return <Overview />;
  }
}

function AuthenticatedApp() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const error = useAuthStore((s) => s.error);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-text-secondary">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login error={error} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AdminLayout>
        <ErrorBoundary>
          <Router />
        </ErrorBoundary>
      </AdminLayout>
    </QueryClientProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
}
